import React, { useEffect, useState } from "react";
import { api, type Property } from "../../api";
import { useAuth } from "../../AuthContext";
import { useToast } from "../../ToastContext";
import { RentReceipt } from "../shared/RentReceipt";
import { ChargeLines } from "../shared/ChargeLines";
import { ElectricityForm, type BillableRoom } from "./ElectricityForm";
import { WhatsAppRemind } from "./WhatsAppRemind";
import { ClaimDetails, RejectClaim, LEDGER_CHANGED_EVENT } from "./ClaimsToConfirm";
import { useLanguage } from "../../i18n";

function fmtINR(n: number) {
  return "₹" + Number(n).toLocaleString("en-IN");
}

function fmtDay(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function fmtDateLong(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function monthAbbr(m: string) {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "short" });
}

function monthName(m: string) {
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function timeAgo(iso: string, t: (key: string, vars?: Record<string, string | number>) => string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return t("ownerLedgers.justNow");
  if (mins < 60) return t("ownerLedgers.minsAgo", { mins });
  const hours = Math.round(mins / 60);
  if (hours < 24) return t("ownerLedgers.hoursAgo", { hours });
  const days = Math.round(hours / 24);
  return t("ownerLedgers.daysAgo", { days });
}

// The last 12 calendar months, oldest first, ending at the current month.
function last12Months(): string[] {
  const out: string[] = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

// Groups PG tenancies by sharing type (Single/Double/...), preserving
// first-seen order. Independent properties have no sharingType, so every
// entry falls into one "" group and callers render a flat list instead.
function groupBySharingType(list: any[]): [string, any[]][] {
  const groups = new Map<string, any[]>();
  for (const item of list) {
    const key = item.sharingType || "";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  }
  return [...groups.entries()];
}

export function OwnerLedgers() {
  const [properties, setProperties] = useState<Property[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [viewingReceipt, setViewingReceipt] = useState<{ entry: any; tenantName: string; justPaid?: boolean } | null>(null);
  const [stats, setStats] = useState<Record<string, { received: number; pending: number }>>({});
  const { user } = useAuth();
  const { t } = useLanguage();

  useEffect(() => {
    api.get("/api/properties/mine").then(async ({ properties }) => {
      setProperties(properties);
      if (properties.length) setSelected(properties[0].id);

      // Same latest-entry-per-tenancy aggregation OwnerHome uses for its
      // Received/Pending header — fetched again here (not shared state)
      // since each screen loads independently.
      const statMap: Record<string, { received: number; pending: number }> = {};
      for (const p of properties as Property[]) {
        let received = 0;
        let pending = 0;
        try {
          const { ledgers } = await api.get(`/api/ledger/property/${p.id}`);
          for (const l of ledgers) {
            const latest = l.entries[0];
            if (!latest) continue;
            if (latest.status === "paid") received += latest.amount;
            else pending += latest.amount;
          }
        } catch {
          // leave this property's stats at zero rather than blocking the rest
        }
        statMap[p.id] = { received, pending };
      }
      setStats(statMap);
    });
  }, []);

  if (!properties) return <div className="spinner" />;
  if (properties.length === 0) return <div className="empty-state">{t("ownerLedgers.emptyNoProperty")}</div>;

  const selectedProperty = properties.find((p) => p.id === selected);

  if (viewingReceipt) {
    return (
      <RentReceipt
        entry={viewingReceipt.entry}
        tenantName={viewingReceipt.tenantName}
        propertyLabel={selectedProperty?.label ?? "—"}
        propertyLocality={selectedProperty?.locality}
        ownerName={user?.name}
        justPaid={viewingReceipt.justPaid}
        onBack={() => setViewingReceipt(null)}
      />
    );
  }

  return (
    <>
      <div className="section-title">{t("ownerLedgers.heading")}</div>
      {properties.map((p) => {
        const open = selected === p.id;
        const s = stats[p.id];
        return (
          <div key={p.id} className="nb-prop-card">
            <div className="nb-prop-header" onClick={() => setSelected(open ? null : p.id)}>
              <div className="nb-prop-name">{p.label}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {s && (s.received > 0 || s.pending > 0) && (
                  <div className="nb-prop-stats">
                    <div className="nb-prop-collected">
                      {fmtINR(s.received)} <small>{t("ownerLedgers.collectedLabel")}</small>
                    </div>
                    {s.pending > 0 && <div className="nb-prop-pending">{fmtINR(s.pending)} {t("ownerLedgers.pendingLabel")}</div>}
                  </div>
                )}
                <span className="nb-prop-chevron" style={{ transform: open ? "rotate(180deg)" : "none" }}>⌄</span>
              </div>
            </div>
            {open && (
              <div className="nb-prop-body">
                <PropertyNotebook
                  propertyId={p.id}
                  property={p}
                  onViewReceipt={(entry, tenantName, justPaid) => setViewingReceipt({ entry, tenantName, justPaid })}
                />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function PropertyNotebook({
  propertyId,
  property,
  onViewReceipt,
}: {
  propertyId: string;
  property: Property;
  onViewReceipt: (entry: any, tenantName: string, justPaid?: boolean) => void;
}) {
  const [data, setData] = useState<{ ledgers: any[]; past: any[] } | null>(null);
  // Held by id, not as a snapshot of the ledger, so adding or removing a
  // charge (which reloads data) shows up in the open detail view immediately.
  const [viewing, setViewing] = useState<{ tenancyId: string; isPast: boolean } | null>(null);
  const [billing, setBilling] = useState<{ rooms?: BillableRoom[]; tenancyId?: string } | null>(null);
  const toast = useToast();
  const { t } = useLanguage();

  async function load() {
    const { ledgers, past } = await api.get(`/api/ledger/property/${propertyId}`);
    setData({ ledgers, past });
  }

  useEffect(() => {
    setData(null);
    setViewing(null);
    load();
  }, [propertyId]);

  // A claim confirmed or rejected from the dashboard card (or the row's own
  // Reject button) should show here without a manual refresh.
  useEffect(() => {
    const refresh = () => load();
    window.addEventListener(LEDGER_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(LEDGER_CHANGED_EVENT, refresh);
  }, [propertyId]);

  if (!data) return <div className="spinner" />;
  const { ledgers, past } = data;
  if (ledgers.length === 0 && past.length === 0) {
    return <div className="empty-state">{t("ownerLedgers.emptyNoTenants")}</div>;
  }

  async function markPaid(entryId: string) {
    const { entry } = await api.patch(`/api/ledger/${entryId}`, { status: "paid", mode: "cash" });
    toast(t("ownerLedgers.markedPaid"));
    load();
    return entry;
  }

  async function sendReminder(entryId: string) {
    const { entry } = await api.post(`/api/ledger/${entryId}/remind`, {});
    toast(t("ownerLedgers.reminderSent"));
    load();
    return entry;
  }

  async function removeCharge(chargeId: string) {
    await api.del(`/api/ledger/charges/${chargeId}`);
    toast(t("ownerLedgers.chargeRemoved"));
    load();
  }

  const viewingLedger = viewing ? (viewing.isPast ? past : ledgers).find((l) => l.tenancyId === viewing.tenancyId) : null;
  if (viewing && viewingLedger) {
    return (
      <>
        <TenancyDetail
          ledger={viewingLedger}
          isPast={viewing.isPast}
          onMarkPaid={viewing.isPast ? undefined : markPaid}
          onSendReminder={viewing.isPast ? undefined : sendReminder}
          onViewReceipt={onViewReceipt}
          onClose={() => setViewing(null)}
          onBillElectricity={viewing.isPast ? undefined : () => setBilling({ tenancyId: viewing.tenancyId })}
          onRemoveCharge={viewing.isPast ? undefined : removeCharge}
        />
        {billing && <ElectricityForm tenancyId={billing.tenancyId} onClose={() => setBilling(null)} onDone={load} />}
      </>
    );
  }

  const isPg = ledgers.some((l) => l.sharingType) || past.some((l) => l.sharingType);
  const billableRooms: BillableRoom[] = [];
  for (const l of ledgers) {
    if (!l.roomId) continue;
    const existing = billableRooms.find((r) => r.roomId === l.roomId);
    if (existing) existing.count++;
    else billableRooms.push({ roomId: l.roomId, roomNumber: l.roomNumber, sharingType: l.sharingType ?? "", count: 1 });
  }
  billableRooms.sort((a, b) => (a.roomNumber ?? 0) - (b.roomNumber ?? 0));

  return (
    <>
      <LateFeeSettings property={property} />
      {isPg && billableRooms.length > 0 && (
        <button className="btn-outline btn-sm" style={{ marginBottom: 12 }} onClick={() => setBilling({ rooms: billableRooms })}>
          ⚡ {t("ownerLedgers.electricityRoomBtn")}
        </button>
      )}
      {billing && <ElectricityForm rooms={billing.rooms} tenancyId={billing.tenancyId} onClose={() => setBilling(null)} onDone={load} />}
      {ledgers.length === 0 && <div className="empty-state">{t("ownerLedgers.emptyNoActiveTenants")}</div>}
      {isPg ? (
        groupBySharingType(ledgers).map(([sharingType, group]) => (
          <NotebookCategory key={sharingType || "—"} title={sharingType || t("ownerLedgers.otherCategory")} count={group.length}>
            {group.map((l) => (
              <TenancyRow
                key={l.tenancyId}
                ledger={l}
                onOpen={() => setViewing({ tenancyId: l.tenancyId, isPast: false })}
                onMarkPaid={markPaid}
                onSendReminder={sendReminder}
                onViewReceipt={onViewReceipt}
              />
            ))}
          </NotebookCategory>
        ))
      ) : (
        ledgers.map((l) => (
          <TenancyRow
            key={l.tenancyId}
            ledger={l}
            onOpen={() => setViewing({ tenancyId: l.tenancyId, isPast: false })}
            onMarkPaid={markPaid}
            onSendReminder={sendReminder}
            onViewReceipt={onViewReceipt}
          />
        ))
      )}

      {past.length > 0 && (
        <>
          <div className="section-title" style={{ margin: "24px 0 10px", fontSize: 12 }}>
            {t("ownerLedgers.pastTenantsHeading", { count: past.length })}
          </div>
          {isPg ? (
            groupBySharingType(past).map(([sharingType, group]) => (
              <NotebookCategory key={sharingType || "—"} title={sharingType || t("ownerLedgers.otherCategory")} count={group.length}>
                {group.map((l) => (
                  <PastTenancyRow key={l.tenancyId} ledger={l} onOpen={() => setViewing({ tenancyId: l.tenancyId, isPast: true })} />
                ))}
              </NotebookCategory>
            ))
          ) : (
            past.map((l) => (
              <PastTenancyRow key={l.tenancyId} ledger={l} onOpen={() => setViewing({ tenancyId: l.tenancyId, isPast: true })} />
            ))
          )}
        </>
      )}
    </>
  );
}

function NotebookCategory({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-row" onClick={() => setOpen((v) => !v)} style={{ cursor: "pointer" }}>
        <b>{title}</b>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="badge vacant">{count}</span>
          <span style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s ease", color: "var(--ink-faint)" }}>
            ›
          </span>
        </div>
      </div>
      {open && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
}

function TenancyGrid({
  entries,
  onViewReceipt,
}: {
  entries: any[];
  onViewReceipt?: (entry: any) => void;
}) {
  const { t } = useLanguage();
  const byMonth = new Map(entries.map((e: any) => [e.month, e]));
  const months = last12Months();
  const paidTotal = entries.filter((e: any) => e.status === "paid").length;

  return (
    <>
      <div className="nb-eyebrow">
        <span>{t("ownerLedgers.rentLedgerLabel")}</span>
        <b>{t("ownerLedgers.paidFraction", { paid: paidTotal, total: entries.length })}</b>
      </div>
      <div className="nb-grid">
        {months.map((m) => {
          const e = byMonth.get(m) as any;
          const cls = !e ? "empty" : e.status === "pending" || e.status === "pending_confirmation" ? e.status : "";
          return (
            <div className={`nb-cell${cls ? " " + cls : ""}`} key={m}>
              <div className="nb-m">{monthAbbr(m)}</div>
              <div className="nb-amt">{e ? fmtINR(e.amount) : "—"}</div>
            </div>
          );
        })}
      </div>

      <div className="nb-entries-label">{t("ownerLedgers.entriesLabel")}</div>
      {entries.slice(0, 8).map((e: any) => (
        <div
          className="nb-entry"
          key={e.id}
          onClick={() => e.status === "paid" && onViewReceipt?.(e)}
          style={{ cursor: e.status === "paid" && onViewReceipt ? "pointer" : "default" }}
        >
          <div className="nb-date">{e.paidOn ? fmtDay(e.paidOn) : monthAbbr(e.month)}</div>
          <div className="nb-mid">
            <div className="nb-title">
              {e.status === "paid"
                ? t("ownerLedgers.rentPaid")
                : e.status === "pending_confirmation"
                  ? t("ownerLedgers.awaitingConfirmation")
                  : t("ownerLedgers.rentDue")}
            </div>
            <div className="nb-sub">{monthName(e.month)}</div>
          </div>
          <div className={`nb-amt ${e.status}`}>{fmtINR(e.amount)}</div>
        </div>
      ))}
    </>
  );
}

function TenancyRow({
  ledger,
  onOpen,
  onMarkPaid,
  onSendReminder,
  onViewReceipt,
}: {
  ledger: any;
  onOpen: () => void;
  onMarkPaid: (entryId: string) => Promise<any>;
  onSendReminder: (entryId: string) => Promise<any>;
  onViewReceipt: (entry: any, tenantName: string, justPaid?: boolean) => void;
}) {
  const latest = ledger.entries[0];
  const [marking, setMarking] = useState(false);
  const [reminding, setReminding] = useState(false);
  const { t } = useLanguage();

  async function markPaid(e: React.MouseEvent) {
    e.stopPropagation();
    setMarking(true);
    try {
      const entry = await onMarkPaid(latest.id);
      onViewReceipt(entry, ledger.tenantName, true);
    } finally {
      setMarking(false);
    }
  }

  async function remind(e: React.MouseEvent) {
    e.stopPropagation();
    setReminding(true);
    try {
      await onSendReminder(latest.id);
    } finally {
      setReminding(false);
    }
  }

  const statusCls = latest?.status === "paid" ? "paid" : latest?.status === "pending_confirmation" ? "pending_confirmation" : "pending";

  return (
    <div className={`tenancy-row-card ${statusCls}`} onClick={onOpen}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div className={`tenancy-avatar ${statusCls}`}>{initials(ledger.tenantName)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="tenancy-name">{ledger.tenantName}</div>
          <div className="tenancy-sub">{ledger.unitLabel ? `${ledger.unitLabel} · ` : ""}{latest ? monthName(latest.month) : ""}</div>
        </div>
        <span className={`tenancy-amt ${statusCls}`}>{fmtINR(latest?.amount ?? 0)}</span>
      </div>
      {latest && <ChargeLines rentAmount={latest.rentAmount} charges={latest.charges} total={latest.amount} />}
      {latest?.status === "pending" && (
        <>
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button className="btn-outline btn-sm" style={{ flex: 1 }} disabled={marking} onClick={markPaid}>
              {marking ? t("ownerLedgers.markingBusy") : t("ownerLedgers.markReceived")}
            </button>
            <button className="btn-outline btn-sm" style={{ flex: 1 }} disabled={reminding} onClick={remind}>
              {reminding ? t("ownerLedgers.sendingBusy") : latest.reminderSentAt ? t("ownerLedgers.remindAgain") : t("ownerLedgers.remind")}
            </button>
          </div>
          <WhatsAppRemind phone={ledger.tenantPhone} tenantName={ledger.tenantName} amount={latest.amount} month={latest.month} />
          {latest.reminderSentAt && (
            <div className="sub" style={{ marginTop: 6 }}>{t("ownerLedgers.remindedAgo", { time: timeAgo(latest.reminderSentAt, t) })}</div>
          )}
        </>
      )}
      {latest?.status === "pending_confirmation" && (
        <>
          <ClaimDetails mode={latest.mode} reference={latest.paymentReference} note={latest.claimNote} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }} onClick={(e) => e.stopPropagation()}>
            <button className="btn-solid btn-sm" style={{ flex: 1 }} disabled={marking} onClick={markPaid}>
              {marking ? t("ownerLedgers.markingBusy") : t("ownerLedgers.confirmReceipt")}
            </button>
            <RejectClaim entryId={latest.id} />
          </div>
        </>
      )}
    </div>
  );
}

function TenancyDetail({
  ledger,
  isPast,
  onMarkPaid,
  onSendReminder,
  onViewReceipt,
  onClose,
  onBillElectricity,
  onRemoveCharge,
}: {
  ledger: any;
  isPast: boolean;
  onMarkPaid?: (entryId: string) => Promise<any>;
  onSendReminder?: (entryId: string) => Promise<any>;
  onViewReceipt: (entry: any, tenantName: string, justPaid?: boolean) => void;
  onClose: () => void;
  onBillElectricity?: () => void;
  onRemoveCharge?: (chargeId: string) => void;
}) {
  const latest = ledger.entries[0];
  const [marking, setMarking] = useState(false);
  const [reminding, setReminding] = useState(false);
  const { t } = useLanguage();

  async function markPaid() {
    if (!onMarkPaid) return;
    setMarking(true);
    try {
      const entry = await onMarkPaid(latest.id);
      onViewReceipt(entry, ledger.tenantName, true);
    } finally {
      setMarking(false);
    }
  }

  async function remind() {
    if (!onSendReminder) return;
    setReminding(true);
    try {
      await onSendReminder(latest.id);
    } finally {
      setReminding(false);
    }
  }

  return (
    <>
      <button className="link-btn" style={{ marginBottom: 14 }} onClick={onClose}>
        {t("ownerLedgers.closeButton")}
      </button>
      <div className="reg-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {ledger.tenantName}
        {ledger.tenantVerified && (
          <span style={{ color: "var(--success)", fontWeight: 800, fontSize: 12 }} title={t("ownerLedgers.verifiedTenantBadge")}>
            &#10003; {t("ownerLedgers.verifiedTenantBadge")}
          </span>
        )}
      </div>
      <div className="reg-sub">
        {ledger.sharingType}
        {ledger.bedLabel ? ` · ${ledger.bedLabel}` : ""}
        {ledger.unitLabel ? ` · ${ledger.unitLabel}` : ""}
        {isPast && `${ledger.sharingType || ledger.bedLabel || ledger.unitLabel ? " · " : ""}${fmtDateLong(ledger.startDate)} → ${ledger.exitDate ? fmtDateLong(ledger.exitDate) : "—"}`}
      </div>

      {isPast && ledger.refundAmount != null && (
        <div className="card">
          <div className="card-row">
            <span className="sub">{t("ownerLedgers.refundLabel")}</span>
            <span className="badge vacant">{fmtINR(ledger.refundAmount)}</span>
          </div>
        </div>
      )}

      {!isPast && ledger.deposit > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-row">
            <span className="sub">{t("ownerLedgers.depositLabel")}</span>
            <span className={`badge ${ledger.depositStatus === "paid" ? "paid" : "pending"}`}>{fmtINR(ledger.deposit)}</span>
          </div>
          {ledger.depositStatus === "paid" && ledger.depositPaidOn && (
            <div className="sub" style={{ marginTop: 4 }}>
              {t("ownerLedgers.depositPaidNote", {
                mode: ledger.depositPaymentMode === "online" ? t("ownerLedgers.depositModeOnline") : t("ownerLedgers.depositModeCash"),
                date: fmtDateLong(ledger.depositPaidOn),
              })}
            </div>
          )}
        </div>
      )}

      {latest && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-row">
            <b>{monthName(latest.month)}</b>
            <span className={`badge ${latest.status}`}>{fmtINR(latest.amount)}</span>
          </div>
          <ChargeLines
            rentAmount={latest.rentAmount}
            charges={latest.charges}
            total={latest.amount}
            onRemove={latest.status === "pending" ? onRemoveCharge : undefined}
          />
        </div>
      )}

      {!isPast && onBillElectricity && (
        <button className="btn-outline btn-sm" style={{ marginBottom: 12 }} onClick={onBillElectricity}>
          ⚡ {t("ownerLedgers.electricityBtn")}
        </button>
      )}

      {!isPast && latest?.status === "pending" && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 0 }}>
            <button className="btn-outline btn-sm" style={{ flex: 1 }} disabled={marking} onClick={markPaid}>
              {marking ? t("ownerLedgers.markingBusy") : t("ownerLedgers.markReceived")}
            </button>
            <button className="btn-outline btn-sm" style={{ flex: 1 }} disabled={reminding} onClick={remind}>
              {reminding ? t("ownerLedgers.sendingBusy") : latest.reminderSentAt ? t("ownerLedgers.remindAgain") : t("ownerLedgers.remind")}
            </button>
          </div>
          <WhatsAppRemind phone={ledger.tenantPhone} tenantName={ledger.tenantName} amount={latest.amount} month={latest.month} spaceBelow={latest.reminderSentAt ? 6 : 16} />
          {latest.reminderSentAt && (
            <div className="sub" style={{ marginBottom: 16 }}>{t("ownerLedgers.remindedAgo", { time: timeAgo(latest.reminderSentAt, t) })}</div>
          )}
        </>
      )}

      {!isPast && latest?.status === "pending_confirmation" && (
        <>
          <ClaimDetails mode={latest.mode} reference={latest.paymentReference} note={latest.claimNote} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "8px 0 16px" }}>
            <button className="btn-solid btn-sm" style={{ flex: 1 }} disabled={marking} onClick={markPaid}>
              {marking ? t("ownerLedgers.markingBusy") : t("ownerLedgers.confirmReceipt")}
            </button>
            <RejectClaim entryId={latest.id} />
          </div>
        </>
      )}

      <TenancyGrid entries={ledger.entries} onViewReceipt={(e) => onViewReceipt(e, ledger.tenantName)} />
    </>
  );
}

function PastTenancyRow({ ledger, onOpen }: { ledger: any; onOpen: () => void }) {
  const { t } = useLanguage();
  return (
    <div
      className="card"
      style={{ opacity: 0.9, cursor: "pointer", background: "var(--surface-alt)", marginBottom: 8 }}
      onClick={onOpen}
    >
      <div className="card-row">
        <div>
          <b style={{ fontSize: 16 }}>{ledger.tenantName}</b>
          <div className="sub">
            {ledger.unitLabel ? `${ledger.unitLabel} · ` : ""}{fmtDateLong(ledger.startDate)} → {ledger.exitDate ? fmtDateLong(ledger.exitDate) : "—"}
          </div>
        </div>
        {ledger.refundAmount != null && (
          <span className="badge vacant">{t("ownerLedgers.refundedAmount", { amount: fmtINR(ledger.refundAmount) })}</span>
        )}
      </div>
    </div>
  );
}

// One card per property: what late fee (if any) applies, with an inline editor.
// The late-fee job itself lives server-side (applyLateFees in reminders.ts).
function LateFeeSettings({ property }: { property: Property }) {
  const { t } = useLanguage();
  const toast = useToast();
  const [amount, setAmount] = useState<number>(property.lateFeeAmount ?? 0);
  const [grace, setGrace] = useState<number>(property.lateFeeGraceDays ?? 3);
  const [editing, setEditing] = useState(false);
  const [draftAmount, setDraftAmount] = useState("");
  const [draftGrace, setDraftGrace] = useState("");
  const [busy, setBusy] = useState(false);

  function startEdit() {
    setDraftAmount(String(amount || ""));
    setDraftGrace(String(grace));
    setEditing(true);
  }

  async function save() {
    const a = Number(draftAmount || 0);
    const g = Number(draftGrace || 0);
    setBusy(true);
    try {
      await api.patch(`/api/properties/${property.id}/late-fee`, { amount: a, graceDays: g });
      setAmount(a);
      setGrace(g);
      setEditing(false);
      toast(t("ownerLedgers.lateFeeSaved"));
    } catch (err) {
      toast(err instanceof Error ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-row">
        <div>
          <div className="sub">{t("ownerLedgers.lateFeeHeading")}</div>
          <b style={{ fontSize: 13 }}>
            {amount > 0 ? t("ownerLedgers.lateFeeSummary", { amount: amount.toLocaleString("en-IN"), days: grace }) : t("ownerLedgers.lateFeeOff")}
          </b>
        </div>
        {!editing && (
          <button type="button" className="link-btn" onClick={startEdit}>
            {t("ownerLedgers.lateFeeEdit")}
          </button>
        )}
      </div>
      {editing && (
        <div style={{ marginTop: 10 }}>
          <div className="field">
            <label>{t("ownerLedgers.lateFeeAmountLabel")}</label>
            <div className="input-wrap">
              <input inputMode="numeric" value={draftAmount} onChange={(e) => setDraftAmount(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="0" />
            </div>
          </div>
          <div className="field">
            <label>{t("ownerLedgers.lateFeeGraceLabel")}</label>
            <div className="input-wrap">
              <input inputMode="numeric" value={draftGrace} onChange={(e) => setDraftGrace(e.target.value.replace(/\D/g, "").slice(0, 2))} placeholder="3" />
            </div>
          </div>
          <button className="btn-solid btn-sm" disabled={busy} onClick={save}>
            {t("ownerLedgers.lateFeeSave")}
          </button>
        </div>
      )}
    </div>
  );
}
